import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { getProficiency, SHORT_MONTHS } from "../../../../../constants/resume";

export default ({ languagesAsText, dateOfBirth, shouldShowExperience, shouldShowEducation, shouldShowSkills, aboveTitleItems, data, getFontFamily, selectedFont, parseHtmlToText, layout }) => {
    const styles = StyleSheet.create({
        page: {
            width: '100%',
            padding: 12,
            fontFamily: getFontFamily(selectedFont),
        },
        contactInfo: {
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 10,
            color: '#444',
            marginBottom: 10,
            gap: 8,
        },
        headerName: {
            fontSize: 32,
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: 2,
            letterSpacing: 0.5,
            fontFamily: getFontFamily(selectedFont),
        },
        headerTitle: {
            fontSize: 14,
            textAlign: 'center',
            marginBottom: 8,
            color: '#222',
        },
        asterisks: {
            textAlign: 'center',
            fontSize: 18,
            letterSpacing: 8,
            color: '#222',
            marginBottom: 8,
        },
        subHeader: {
            textAlign: 'center',
            fontSize: 10,
            color: '#888',
            marginBottom: 16,
        },
        sectionTitle: {
            fontSize: 11,
            fontWeight: 600,
            textAlign: 'left',
            marginTop: 18,
            marginBottom: 8,
            letterSpacing: 1,
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderTopColor: '#222',
            borderBottomColor: '#222',
            borderTopStyle: 'solid',
            borderBottomStyle: 'solid',
            paddingTop: 4,
            paddingBottom: 2,
            backgroundColor: 'transparent',
        },
        leftCol: {
            width: '38%',
            paddingRight: 24,
        },
        rightCol: {
            width: '62%',
        },
        mainSections: {
            flexDirection: 'row',
            width: '100%',
            marginTop: 5,
        },
        skillItem: {
            fontSize: 11,
            marginBottom: 6,
        },
        langTitle: {
            fontWeight: 'bold',
            fontSize: 12,
            marginTop: 18,
            marginBottom: 4,
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderTopColor: '#222',
            borderBottomColor: '#222',
            borderTopStyle: 'solid',
            borderBottomStyle: 'solid',
            paddingTop: 2,
            paddingBottom: 2,
        },
        langItem: {
            fontSize: 11,
            marginBottom: 4,
        },
        jobList: {
            marginTop: 8,
        },
        jobDesc: {
            fontSize: 9,
            marginBottom: 6,
            marginLeft: 8,
        },
        bullet: {
            fontSize: 16,
            marginRight: 6,
            lineHeight: 1,
        },
        eduSchool: {
            fontWeight: 'bold',
            fontSize: 12,
            marginBottom: 2,
        },
        eduMeta: {
            fontSize: 9,
            color: '#888',
            marginBottom: 2,
        },
        eduDesc: {
            fontSize: 9,
            color: '#222',
            marginBottom: 6,
        },
    });

    // Compose contact info
    const contactItems = [
        data.city,
        data.postalCode,
        data.email
    ].filter(Boolean);

    // Compose subheader info
    const subHeaderItems = [
        data.nationality && `${data.nationality}`,
        data.driversLicense && `${data.driversLicense}`,
        dateOfBirth && `${dateOfBirth}`,
    ].filter(Boolean).join('   •   ');

    return (
        <View style={styles.page}>
            {/* Contact Info */}
            {contactItems.length > 0 && (
                <View style={styles.contactInfo}>
                    {contactItems.map((item, idx) => (
                        <Text key={idx}>
                            {item}{idx < contactItems.length - 1 ? '  •  ' : ''}
                        </Text>
                    ))}
                </View>
            )}
            {/* Name */}
            <Text style={styles.headerName}>{`${data.firstName || ''} ${data.lastName || ''}`.trim()}</Text>
            {/* Title */}
            {data.title && <Text style={styles.headerTitle}>{data.title}</Text>}
            {/* 3 big asterisks */}
            <Text style={styles.asterisks}>* * *</Text>
            {/* Subheader: nationality, driving license, birth date */}
            {subHeaderItems && <Text style={styles.subHeader}>{subHeaderItems}</Text>}

            {/* Main content: left and right columns */}
            <View style={styles.mainSections}>
                {/* Left column: Skills, Languages */}
                <View style={styles.leftCol}>
                    {/* Skills */}
                    {shouldShowSkills && (
                        <View>
                            <Text style={styles.sectionTitle}>SKILLS</Text>
                            {(data.skills || []).map((skill, idx) => (
                                <Text style={styles.skillItem} key={idx}>{skill.name}</Text>
                            ))}
                        </View>
                    )}
                    {/* Languages */}
                    {languagesAsText?.length > 0 && (
                        <View>
                            <Text style={styles.sectionTitle}>LANGUAGES</Text>
                            <Text style={styles.langItem}>{languagesAsText}</Text>
                        </View>
                    )}
                </View>
                {/* Right column: Experience, Education */}
                <View style={styles.rightCol}>
                    {/* Employment History */}
                    {shouldShowExperience && (
                        <View>
                            <Text style={styles.sectionTitle}>EMPLOYMENT HISTORY</Text>
                            <View style={styles.jobList}>
                                {data.experience.map((job, idx) => (
                                    <View key={idx}>
                                        <View style={styles.bigSectionHeaderLeft}>
                                            <Text style={styles.eduSchool}>{job.jobPosition}</Text>
                                            <View style={{ display: 'flex', flexDirection: 'row' }}>
                                                <Text style={styles.eduMeta}>{`${job.companyName} `}</Text>
                                                {(job.startDate?.month || job.startDate?.year) && (
                                                    <Text style={styles.eduMeta}>
                                                        {`   - `}
                                                        {job.startDate?.month ? `${SHORT_MONTHS[job.startDate.month - 1]} ` : ''}
                                                        {job.startDate?.year} - {job.isPresent ? 'Present' : `${job.endDate?.month ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][job.endDate.month - 1] + ' ' : ''}${job.endDate?.year || ''}`}
                                                    </Text>
                                                )}
                                            </View>
                                        </View>
                                        <View style={styles.bigSectionHeaderRight}>

                                            {job.location && <Text style={styles.location}>{job.location}</Text>}
                                        </View>
                                        {job.description && parseHtmlToText(job.description, styles.jobDesc)}
                                    </View>
                                ))}
                            </View>
                        </View>
                    )}
                    {/* Education */}
                    {shouldShowEducation && (
                        <View>
                            <Text style={styles.sectionTitle}>EDUCATION</Text>
                            {data.education.map((ed, idx) => (
                                <View key={idx}>
                                    <Text style={styles.eduSchool}>{ed.degreeName}</Text>
                                    <Text style={styles.eduMeta}>{ed.universityName}{ed.location ? `, ${ed.location}` : ''} — {ed.startDate?.month ? `${SHORT_MONTHS[ed.startDate.month - 1]} ` : ''}{ed.startDate?.year} - {ed.endDate?.month ? `${SHORT_MONTHS[ed.endDate.month - 1]} ` : ''}{ed.endDate?.year}</Text>
                                    {ed.description && <Text style={styles.eduDesc}>{parseHtmlToText(ed.description, styles.eduDesc)}</Text>}
                                </View>
                            ))}
                        </View>
                    )}
                </View>
            </View>
        </View>
    );
} 